import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { Authentication } from "../../types/types";
import { logout } from "./authSlice";
import { Capacitor } from "@capacitor/core";

const VITE_OPEX_SVC_URL = import.meta.env.VITE_OPEX_SVC_URL;
const TERMS_STORAGE_KEY = "allaria-terms";

export const TERMS_AND_CONDITIONS_VERSION = "1.0";
export const PRIVACY_POLICY_VERSION = "1.0";

// ─── Types ───────────────────────────────────────────────────────────────────

interface TermsAndConditionsResponse {
  account_id?: string;
  version?: string;
  privacy_policy_version?: string;
  signed_at?: string;
}

export interface TermsAndConditionsState {
  hasAccepted: boolean | null; // null = not yet fetched
  loading: boolean;
  error: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getAuthToken = (getState: () => unknown): string | null => {
  const state = getState() as { authentication: Authentication };
  return state.authentication?.auth?.access_token ?? null;
};

// Only hydrate on web — on native the session is managed via biometrics
const allowLocalHydration = !Capacitor.isNativePlatform();

const readFromStorage = (): boolean | null => {
  if (!allowLocalHydration) return null;
  try {
    const stored = localStorage.getItem(TERMS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// ─── Thunks ──────────────────────────────────────────────────────────────────

export const fetchTermsAndConditionsStatus = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>(
  "termsAndConditions/fetchStatus",
  async (_, { getState, rejectWithValue }) => {
    const token = getAuthToken(getState);
    if (!token) return rejectWithValue("No authentication token available");

    try {
      const response = await axios.get<TermsAndConditionsResponse>(
        `${VITE_OPEX_SVC_URL}/broker/document/terms-and-conditions`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const accepted =
        !!response.data?.signed_at &&
        response.data?.version === TERMS_AND_CONDITIONS_VERSION &&
        response.data?.privacy_policy_version === PRIVACY_POLICY_VERSION;

      return accepted;
    } catch {
      return false;
    }
  },
);

// ─── Slice ───────────────────────────────────────────────────────────────────

const initialState: TermsAndConditionsState = {
  hasAccepted: readFromStorage(), // Hydrate from localStorage on app start
  loading: false,
  error: null,
};

const termsAndConditionsSlice = createSlice({
  name: "termsAndConditions",
  initialState,
  reducers: {
    setTermsAccepted: (state) => {
      state.hasAccepted = true;
      if (allowLocalHydration) {
        localStorage.setItem(TERMS_STORAGE_KEY, JSON.stringify(true));
      }
    },
    resetTermsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, () => {
        if (allowLocalHydration) {
          localStorage.removeItem(TERMS_STORAGE_KEY);
        }
        return { hasAccepted: null, loading: false, error: null };
      })
      .addCase(fetchTermsAndConditionsStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTermsAndConditionsStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.hasAccepted = action.payload;
        if (allowLocalHydration) {
          localStorage.setItem(
            TERMS_STORAGE_KEY,
            JSON.stringify(action.payload),
          );
        }
      })
      .addCase(fetchTermsAndConditionsStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Unknown error";
      });
  },
});

export const { setTermsAccepted, resetTermsState } =
  termsAndConditionsSlice.actions;
export default termsAndConditionsSlice.reducer;
